import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ArrowLeft, Trash, Eye, Camera, FilmStrip, Crown, CreditCard, CheckCircle, ChartBar, PaintBrush, Microphone, VideoCamera, Upload, ArrowClockwise, FloppyDisk, Palette, TextT, Image as ImageIcon, Gear } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── FONT MAP ───
const FONT_MAP = {
  script: "'Playfair Display', serif",
  sans: "'Inter', sans-serif",
  serif: "'Georgia', serif",
};

// ─── PRESET BG STYLES ───
const PRESET_BG = {
  wedding: {
    background: `linear-gradient(180deg, rgba(244,114,182,0.3) 0%, rgba(251,191,36,0.15) 50%, rgba(120,53,15,0.4) 100%),
                 radial-gradient(circle at 30% 20%, rgba(251,191,36,0.3), transparent 50%),
                 radial-gradient(circle at 70% 80%, rgba(244,114,182,0.2), transparent 50%),
                 #2D1B2E`,
  },
  party: {
    background: `linear-gradient(135deg, #0F0723 0%, #1A0A3E 50%, #0D0D0D 100%),
                 radial-gradient(circle at 20% 50%, rgba(168,85,247,0.3), transparent 50%),
                 radial-gradient(circle at 80% 30%, rgba(245,158,11,0.2), transparent 50%)`,
  },
  corporate: {
    background: 'linear-gradient(180deg, #F1F5F9, #E2E8F0)',
  },
};

// ═══════════════════════════════════════
// GENERAL TAB
// ═══════════════════════════════════════
const GeneralTab = ({ data, navigate, fetchOverview }) => {
  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (eventId, eventName) => {
    if (!window.confirm(`Delete "${eventName}" and all its photos? This cannot be undone.`)) return;
    setDeleting(eventId);
    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success(`Deleted "${eventName}"`);
      fetchOverview();
    } catch (e) {
      toast.error('Failed to delete event');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div>
      {/* Summary Cards */}
      {data?.totals && (
        <div className="grid grid-cols-3 gap-4 mb-10" data-testid="admin-totals">
          <div className="rounded-2xl border border-white/10 bg-[#15161B] p-6 text-center">
            <ChartBar size={28} weight="bold" className="text-[#B89762] mx-auto mb-2" />
            <p className="text-3xl font-bold text-white">{data.totals.event_count}</p>
            <p className="text-sm text-gray-500 font-medium">Total Events</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#15161B] p-6 text-center">
            <Camera size={28} weight="bold" className="text-[#B89762] mx-auto mb-2" />
            <p className="text-3xl font-bold text-white">{data.totals.total_uploads}</p>
            <p className="text-sm text-gray-500 font-medium">Total Uploads</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#15161B] p-6 text-center">
            <Eye size={28} weight="bold" className="text-blue-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-white">{data.totals.total_views}</p>
            <p className="text-sm text-gray-500 font-medium">Total Views</p>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10" data-testid="admin-quick-actions">
        <button
          onClick={() => navigate('/admin/event-galleries')}
          className="rounded-2xl p-5 text-left transition-all hover:scale-[1.01]"
          style={{
            background: 'linear-gradient(135deg, rgba(212,170,82,0.18), rgba(184,151,98,0.08))',
            border: '1px solid rgba(212,170,82,0.42)',
          }}
          data-testid="admin-galleries-link"
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D4AF5E]">Event Galleries ✦</span>
          </div>
          <p className="text-base font-bold text-white mb-0.5">Create &amp; publish event galleries →</p>
          <p className="text-xs text-gray-400">Under 2 minutes per event · drag-and-drop photos</p>
        </button>
        <button
          onClick={() => navigate('/admin/photobooth-media')}
          className="rounded-2xl p-5 text-left transition-all hover:scale-[1.01]"
          style={{
            background: 'linear-gradient(135deg, rgba(212,170,82,0.18), rgba(184,151,98,0.08))',
            border: '1px solid rgba(212,170,82,0.42)',
          }}
          data-testid="admin-photobooth-media-link"
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D4AF5E]">Photobooth Media ✦</span>
          </div>
          <p className="text-base font-bold text-white mb-0.5">Cover photos for /photobooth-hire →</p>
          <p className="text-xs text-gray-400">Booths · packages · add-ons · upload &amp; clear</p>
        </button>
        <button
          onClick={() => navigate('/admin/historic-events')}
          className="rounded-2xl p-5 text-left transition-all hover:scale-[1.01]"
          style={{
            background: 'linear-gradient(135deg, rgba(212,170,82,0.24), rgba(184,151,98,0.10))',
            border: '1px solid rgba(212,170,82,0.62)',
          }}
          data-testid="admin-historic-events-link"
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D4AF5E]">Historic Events ✦</span>
          </div>
          <p className="text-base font-bold text-white mb-0.5">Portfolio builder →</p>
          <p className="text-xs text-gray-400">Add past events · auto-geocode · drops a pin on the map</p>
        </button>
        <button
          onClick={() => navigate('/admin/backdrops')}
          className="rounded-2xl p-5 text-left transition-all hover:scale-[1.01]"
          style={{
            background: 'linear-gradient(135deg, rgba(212,170,82,0.18), rgba(184,151,98,0.08))',
            border: '1px solid rgba(212,170,82,0.42)',
          }}
          data-testid="admin-backdrops-link"
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D4AF5E]">Backdrop Hire ✦</span>
          </div>
          <p className="text-base font-bold text-white mb-0.5">Backdrop catalogue →</p>
          <p className="text-xs text-gray-400">Edit prices, photos, ordering · add new backdrops</p>
        </button>
        <button
          onClick={() => navigate('/admin/heritage')}
          className="rounded-2xl p-5 text-left transition-all hover:scale-[1.01]"
          style={{
            background: 'linear-gradient(135deg, rgba(85,107,47,0.20), rgba(122,92,62,0.10))',
            border: '1px solid rgba(122,92,62,0.42)',
          }}
          data-testid="admin-heritage-link"
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D4AF5E]">Heritage Collection ✦</span>
          </div>
          <p className="text-base font-bold text-white mb-0.5">Signature catalogue →</p>
          <p className="text-xs text-gray-400">Edit products &amp; packages · upload photos · flip Coming Soon → Live</p>
        </button>
        <button
          onClick={() => navigate('/admin/inventory')}
          className="rounded-2xl p-5 text-left transition-all hover:scale-[1.01]"
          style={{
            background: 'linear-gradient(135deg, rgba(212,170,82,0.18), rgba(184,151,98,0.08))',
            border: '1px solid rgba(212,170,82,0.42)',
          }}
          data-testid="admin-inventory-link"
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D4AF5E]">Inventory ✦</span>
          </div>
          <p className="text-base font-bold text-white mb-0.5">Live stock control →</p>
          <p className="text-xs text-gray-400">Confirm or release bookings · drives customer-facing availability</p>
        </button>
        <button
          onClick={() => navigate('/admin/bookings')}
          className="rounded-2xl p-5 text-left transition-all hover:scale-[1.01]"
          style={{
            background: 'linear-gradient(135deg, rgba(212,170,82,0.18), rgba(184,151,98,0.08))',
            border: '1px solid rgba(212,170,82,0.42)',
          }}
          data-testid="admin-bookings-link"
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D4AF5E]">Bookings ✦</span>
          </div>
          <p className="text-base font-bold text-white mb-0.5">Date-based booking pipeline →</p>
          <p className="text-xs text-gray-400">Convert enquiries · confirm dates · auto-deduct inventory</p>
        </button>
        <button
          onClick={() => navigate('/admin/reviews')}
          className="rounded-2xl p-5 text-left transition-all hover:scale-[1.01]"
          style={{
            background: 'linear-gradient(135deg, rgba(212,170,82,0.18), rgba(122,92,62,0.10))',
            border: '1px solid rgba(212,170,82,0.42)',
          }}
          data-testid="admin-reviews-link"
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D4AF5E]">Reviews ✦</span>
          </div>
          <p className="text-base font-bold text-white mb-0.5">Trust hub &amp; testimonials →</p>
          <p className="text-xs text-gray-400">Approve · feature · import existing · per-booking review links + QR</p>
        </button>
        <button
          onClick={() => navigate('/admin/quotes')}
          className="rounded-2xl p-5 text-left transition-all hover:scale-[1.01]"
          style={{
            background: 'linear-gradient(135deg, rgba(212,170,82,0.18), rgba(184,151,98,0.08))',
            border: '1px solid rgba(212,170,82,0.42)',
          }}
          data-testid="admin-quotes-link"
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D4AF5E]">Quotes ✦</span>
          </div>
          <p className="text-base font-bold text-white mb-0.5">Quote archive →</p>
          <p className="text-xs text-gray-400">Draft · sent · accepted · branded PDF · email customer</p>
        </button>
        <button
          onClick={() => navigate('/admin/comp')}
          className="rounded-2xl p-5 text-left transition-all hover:scale-[1.01]"
          style={{
            background: 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(220,38,38,0.08))',
            border: '1px solid rgba(245, 158, 11, 0.3)',
          }}
          data-testid="admin-comp-link"
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-300">Comp Studio Pass</span>
          </div>
          <p className="text-base font-bold text-white mb-0.5">Mint a free pass for any supplier →</p>
          <p className="text-xs text-gray-400">Pre-applies a Stripe promo code · forwardable link</p>
        </button>
        <button
          onClick={() => navigate('/admin/growth')}
          className="rounded-2xl p-5 text-left transition-all hover:scale-[1.01]"
          style={{
            background: 'linear-gradient(135deg, rgba(184,151,98,0.12), rgba(194,157,82,0.08))',
            border: '1px solid rgba(184, 151, 98, 0.3)',
          }}
          data-testid="admin-growth-link"
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#C29D52]">Growth Dashboard</span>
          </div>
          <p className="text-base font-bold text-white mb-0.5">Funnel &amp; viral metrics →</p>
          <p className="text-xs text-gray-400">RSVP feeds · share links · conversion</p>
        </button>
      </div>

      {/* Events Table */}
      <div className="rounded-2xl border border-white/10 bg-[#15161B] overflow-hidden" data-testid="admin-events-table">
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="font-semibold text-gray-300">All Events</h3>
        </div>
        {data?.events?.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No events yet</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {data?.events?.map((ev) => (
              <div key={ev.event_id} className="px-6 py-4 flex items-center justify-between hover:bg-white/5/50 transition-colors" data-testid={`admin-event-${ev.event_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-white truncate">{ev.event_name}</h4>
                    {ev.package_type === 'premium' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-[#B89762]/100/100/20 text-[#C29D52]"><Crown size={10} weight="fill" />PREMIUM</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded text-xs font-bold bg-white/5 text-gray-400">STANDARD</span>
                    )}
                    {ev.payment_status === 'paid' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-green-500/20 text-green-400"><CheckCircle size={10} weight="fill" />PAID</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-amber-500/100/20 text-amber-600"><CreditCard size={10} weight="bold" />PENDING</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>{ev.event_date}</span>
                    <span className="flex items-center gap-1"><Camera size={14} /> {ev.upload_count} uploads</span>
                    <span className="flex items-center gap-1"><Eye size={14} /> {ev.views} views</span>
                    {ev.film_status !== 'none' && (
                      <span className="flex items-center gap-1"><FilmStrip size={14} /> Film: {ev.film_status}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button onClick={() => navigate(`/event/${ev.slug}`)} className="p-2 rounded-lg hover:bg-white/5 transition-colors text-gray-400" title="View" data-testid={`view-event-${ev.event_id}`}>
                    <Eye size={20} weight="bold" />
                  </button>
                  <button onClick={() => handleDelete(ev.event_id, ev.event_name)} disabled={deleting === ev.event_id} className="p-2 rounded-lg hover:bg-red-50 transition-colors text-red-500 disabled:opacity-50" title="Delete" data-testid={`delete-event-${ev.event_id}`}>
                    {deleting === ev.event_id ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-red-500 border-t-transparent"></div> : <Trash size={20} weight="bold" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════
// DESIGN & BRANDING TAB (3-Panel Builder)
// ═══════════════════════════════════════
const DEFAULT_CONFIG = {
  title: 'Leave a Message',
  subtitle: 'Tap to leave a 30 second message!',
  bg_type: 'preset',
  bg_value: 'wedding',
  btn_audio_label: 'Record Audio',
  btn_video_label: 'Record Video',
  btn_audio_color: '#F87171',
  btn_video_color: '#38BDF8',
  title_font: 'script',
  title_color: '#FFFFFF',
  subtitle_color: '#FFFFFF',
  show_settings_btn: true,
};

const DesignTab = ({ events }) => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [config, setConfig] = useState({ ...DEFAULT_CONFIG });
  const [saving, setSaving] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const fileInputRef = useRef(null);

  // Load config when event selected
  const loadConfig = useCallback(async (eventId) => {
    setLoadingConfig(true);
    try {
      const res = await axios.get(`${API}/events/${eventId}/screen-config`);
      setConfig({ ...DEFAULT_CONFIG, ...res.data });
    } catch {
      setConfig({ ...DEFAULT_CONFIG });
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  useEffect(() => {
    if (events?.length > 0 && !selectedEvent) {
      setSelectedEvent(events[0].event_id);
      loadConfig(events[0].event_id);
    }
  }, [events, selectedEvent, loadConfig]);

  const handleEventChange = (eventId) => {
    setSelectedEvent(eventId);
    loadConfig(eventId);
  };

  const updateField = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const applyPreset = (presetId) => {
    const presets = {
      wedding: { ...DEFAULT_CONFIG, preset: 'wedding' },
      party: {
        title: 'Drop a Message', subtitle: 'Tap to leave a 30 second message!',
        bg_type: 'preset', bg_value: 'party',
        btn_audio_label: 'Record Audio', btn_video_label: 'Record Video',
        btn_audio_color: '#A855F7', btn_video_color: '#F59E0B',
        title_font: 'sans', title_color: '#FFFFFF', subtitle_color: '#E5E5E5',
        show_settings_btn: true, preset: 'party',
      },
      corporate: {
        title: 'Leave Your Feedback', subtitle: 'Tap to record a 30 second message',
        bg_type: 'preset', bg_value: 'corporate',
        btn_audio_label: 'Record Audio', btn_video_label: 'Record Video',
        btn_audio_color: '#3B82F6', btn_video_color: '#10B981',
        title_font: 'sans', title_color: '#1E293B', subtitle_color: '#475569',
        show_settings_btn: true, preset: 'corporate',
      },
    };
    setConfig(presets[presetId] || { ...DEFAULT_CONFIG });
  };

  const handleSave = async () => {
    if (!selectedEvent) return;
    setSaving(true);
    try {
      await axios.put(`${API}/events/${selectedEvent}/screen-config`, config);
      toast.success('Screen design saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig({ ...DEFAULT_CONFIG });
  };

  const handleBgUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedEvent) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/events/${selectedEvent}/screen-bg-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateField('bg_type', 'upload');
      updateField('bg_value', res.data.bg_url);
      toast.success('Background uploaded');
    } catch {
      toast.error('Upload failed');
    }
  };

  // Build the live preview background style
  const getPreviewBgStyle = () => {
    if (config.bg_type === 'upload' && config.bg_value) {
      return {
        backgroundImage: `url(${BACKEND_URL}${config.bg_value})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }
    if (config.bg_type === 'color' && config.bg_value) {
      return { background: config.bg_value };
    }
    return PRESET_BG[config.bg_value] || PRESET_BG.wedding;
  };

  if (!events?.length) {
    return (
      <div className="text-center py-20 text-gray-500" data-testid="no-events-design">
        <PaintBrush size={48} weight="bold" className="mx-auto mb-4 text-gray-300" />
        <p className="font-semibold text-lg mb-1">No events to customize</p>
        <p className="text-sm">Create an event first to access the screen builder.</p>
      </div>
    );
  }

  return (
    <div data-testid="design-branding-tab">
      {/* Event Selector */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-400 mb-2">Select Event</label>
        <select
          value={selectedEvent || ''}
          onChange={(e) => handleEventChange(e.target.value)}
          className="w-full max-w-sm px-4 py-2.5 rounded-xl border border-white/10 bg-[#15161B] text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#B89762]/30"
          data-testid="event-selector"
        >
          {events.map((ev) => (
            <option key={ev.event_id} value={ev.event_id}>{ev.event_name}</option>
          ))}
        </select>
      </div>

      {loadingConfig ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#B89762]/40 border-t-transparent"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="three-panel-builder">

          {/* ─── LEFT PANEL: CONTROLS ─── */}
          <div className="lg:col-span-3 space-y-5" data-testid="controls-panel">
            <div className="rounded-2xl border border-white/10 bg-[#15161B] p-5">
              <h4 className="font-bold text-white text-sm mb-4 flex items-center gap-2">
                <TextT size={16} weight="bold" className="text-[#B89762]" /> Text
              </h4>

              <label className="block text-xs font-semibold text-gray-500 mb-1">Title</label>
              <input
                type="text"
                value={config.title}
                onChange={(e) => updateField('title', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white mb-3 focus:outline-none focus:ring-2 focus:ring-[#B89762]/30"
                data-testid="title-input"
              />

              <label className="block text-xs font-semibold text-gray-500 mb-1">Subtitle</label>
              <input
                type="text"
                value={config.subtitle}
                onChange={(e) => updateField('subtitle', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white mb-3 focus:outline-none focus:ring-2 focus:ring-[#B89762]/30"
                data-testid="subtitle-input"
              />

              <label className="block text-xs font-semibold text-gray-500 mb-1">Title Font</label>
              <select
                value={config.title_font}
                onChange={(e) => updateField('title_font', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white mb-3 focus:outline-none focus:ring-2 focus:ring-[#B89762]/30"
                data-testid="font-select"
              >
                <option value="script">Script (Playfair)</option>
                <option value="sans">Sans (Inter)</option>
                <option value="serif">Serif (Georgia)</option>
              </select>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Title Color</label>
                  <input type="color" value={config.title_color} onChange={(e) => updateField('title_color', e.target.value)} className="w-full h-9 rounded-lg border border-white/10 cursor-pointer" data-testid="title-color" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Subtitle Color</label>
                  <input type="color" value={config.subtitle_color} onChange={(e) => updateField('subtitle_color', e.target.value)} className="w-full h-9 rounded-lg border border-white/10 cursor-pointer" data-testid="subtitle-color" />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#15161B] p-5">
              <h4 className="font-bold text-white text-sm mb-4 flex items-center gap-2">
                <Palette size={16} weight="bold" className="text-[#B89762]" /> Buttons
              </h4>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Audio Button Label</label>
              <input type="text" value={config.btn_audio_label} onChange={(e) => updateField('btn_audio_label', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white mb-3 focus:outline-none focus:ring-2 focus:ring-[#B89762]/30" data-testid="audio-label-input" />

              <label className="block text-xs font-semibold text-gray-500 mb-1">Video Button Label</label>
              <input type="text" value={config.btn_video_label} onChange={(e) => updateField('btn_video_label', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white mb-3 focus:outline-none focus:ring-2 focus:ring-[#B89762]/30" data-testid="video-label-input" />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Audio Color</label>
                  <input type="color" value={config.btn_audio_color} onChange={(e) => updateField('btn_audio_color', e.target.value)} className="w-full h-9 rounded-lg border border-white/10 cursor-pointer" data-testid="audio-color" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Video Color</label>
                  <input type="color" value={config.btn_video_color} onChange={(e) => updateField('btn_video_color', e.target.value)} className="w-full h-9 rounded-lg border border-white/10 cursor-pointer" data-testid="video-color" />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#15161B] p-5">
              <h4 className="font-bold text-white text-sm mb-4 flex items-center gap-2">
                <ImageIcon size={16} weight="bold" className="text-blue-500" /> Background
              </h4>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-white/10 text-sm font-medium text-gray-500 hover:border-[#C29D52] hover:text-[#C29D52] transition-colors"
                data-testid="upload-bg-btn"
              >
                <Upload size={16} weight="bold" /> Upload Image
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleBgUpload} data-testid="bg-file-input" />
              <p className="text-xs text-gray-400 mt-2 text-center">Max 10MB. JPG, PNG, WebP</p>

              <div className="mt-3">
                <label className="block text-xs font-semibold text-gray-500 mb-1">Or solid color</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.bg_type === 'color' ? config.bg_value : '#2D1B2E'} onChange={(e) => { updateField('bg_type', 'color'); updateField('bg_value', e.target.value); }} className="w-9 h-9 rounded-lg border border-white/10 cursor-pointer" data-testid="bg-color-picker" />
                  <span className="text-xs text-gray-400">Pick a background color</span>
                </div>
              </div>
            </div>
          </div>

          {/* ─── CENTER PANEL: LIVE PREVIEW ─── */}
          <div className="lg:col-span-6 flex flex-col items-center" data-testid="preview-panel">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Live Preview</p>

            {/* Tablet Frame */}
            <div className="relative" style={{ width: 380, height: 520 }}>
              <div
                className="absolute inset-0 rounded-[28px] bg-[#15161B]"
                style={{ padding: 14, boxShadow: '0 25px 60px rgba(0,0,0,0.15), inset 0 0 0 1px rgba(255,255,255,0.08)' }}
              >
                <div className="w-full h-full rounded-[16px] overflow-hidden" style={getPreviewBgStyle()} data-testid="live-preview-screen">
                  <div className="w-full h-full flex flex-col items-center justify-center px-6">
                    {/* Title */}
                    <h2
                      className="text-center leading-tight mb-1"
                      style={{
                        fontFamily: FONT_MAP[config.title_font] || FONT_MAP.script,
                        fontSize: '26px',
                        fontWeight: config.title_font === 'script' ? 700 : 800,
                        color: config.title_color,
                        textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      }}
                      data-testid="preview-title"
                    >
                      {config.title}
                    </h2>

                    {/* Buttons */}
                    <div className="flex gap-5 mt-6">
                      <div
                        className="flex flex-col items-center justify-center rounded-2xl shadow-lg cursor-default"
                        style={{ width: 100, height: 100, background: config.btn_audio_color }}
                        data-testid="preview-audio-btn"
                      >
                        <Microphone size={32} weight="bold" className="text-white" />
                        <span className="text-white text-[11px] font-semibold mt-1.5 text-center leading-tight px-1">{config.btn_audio_label}</span>
                      </div>
                      <div
                        className="flex flex-col items-center justify-center rounded-2xl shadow-lg cursor-default"
                        style={{ width: 100, height: 100, background: config.btn_video_color }}
                        data-testid="preview-video-btn"
                      >
                        <VideoCamera size={32} weight="bold" className="text-white" />
                        <span className="text-white text-[11px] font-semibold mt-1.5 text-center leading-tight px-1">{config.btn_video_label}</span>
                      </div>
                    </div>

                    {/* Subtitle */}
                    <p
                      className="mt-5 text-center"
                      style={{ fontSize: '13px', color: config.subtitle_color, textShadow: '0 1px 4px rgba(0,0,0,0.2)' }}
                      data-testid="preview-subtitle"
                    >
                      {config.subtitle}
                    </p>

                    {/* Settings btn */}
                    {config.show_settings_btn && (
                      <div className="absolute bottom-8 right-8 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/30 backdrop-blur-sm" data-testid="preview-settings-btn">
                        <Gear size={12} weight="bold" className="text-white/70" />
                        <span className="text-white/70 text-[10px] font-medium">Settings</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ─── RIGHT PANEL: QUICK ACTIONS ─── */}
          <div className="lg:col-span-3 space-y-5" data-testid="actions-panel">
            <div className="rounded-2xl border border-white/10 bg-[#15161B] p-5">
              <h4 className="font-bold text-white text-sm mb-4">Quick Actions</h4>

              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white transition-all hover:scale-[1.02] disabled:opacity-50 mb-3"
                style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
                data-testid="save-config-btn"
              >
                <FloppyDisk size={18} weight="bold" />
                {saving ? 'Saving...' : 'Save Design'}
              </button>

              <button
                onClick={handleReset}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-gray-400 border border-white/10 hover:bg-white/5 transition-colors"
                data-testid="reset-config-btn"
              >
                <ArrowClockwise size={16} weight="bold" />
                Reset to Default
              </button>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#15161B] p-5">
              <h4 className="font-bold text-white text-sm mb-4">Theme Presets</h4>
              <div className="space-y-2">
                {[
                  { id: 'wedding', label: 'Wedding', dot: 'bg-rose-400' },
                  { id: 'party', label: 'Party', dot: 'bg-[#B89762]/40' },
                  { id: 'corporate', label: 'Corporate', dot: 'bg-blue-400' },
                ].map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => applyPreset(preset.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-white/10 text-sm font-semibold text-white bg-white/5 hover:bg-white/10 transition-all ${config.bg_value === preset.id && config.bg_type === 'preset' ? 'ring-2 ring-[#B89762] border-[#B89762]/50' : ''}`}
                    data-testid={`preset-${preset.id}`}
                  >
                    <div className={`w-3 h-3 rounded-full ${preset.dot}`}></div>
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#15161B] p-5">
              <h4 className="font-bold text-white text-sm mb-3">Options</h4>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.show_settings_btn}
                  onChange={(e) => updateField('show_settings_btn', e.target.checked)}
                  className="w-4 h-4 rounded border-white/20 text-[#B89762] focus:ring-[#B89762]/30"
                  data-testid="settings-toggle"
                />
                <span className="text-sm text-gray-400">Show Settings button</span>
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════
// MUSIC LIBRARY TAB
// ═══════════════════════════════════════
const MusicLibraryTab = () => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: '', mood: 'upbeat', attribution: '', is_premium: false });
  const fileRef = useRef(null);

  const MOODS = ['romantic', 'upbeat', 'cinematic', 'corporate', 'party', 'chill', 'emotional'];

  const load = async () => {
    try { const r = await axios.get(`${API}/music/library`); setTracks(r.data || []); }
    catch { toast.error('Failed to load library'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!form.name.trim()) { toast.error('Enter a track name first'); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error('File too large (25 MB max)'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', form.name);
      fd.append('mood', form.mood);
      fd.append('attribution', form.attribution);
      fd.append('is_premium', form.is_premium ? 'true' : 'false');
      await axios.post(`${API}/music/library`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Added "${form.name}" to library`);
      setForm({ name: '', mood: form.mood, attribution: '', is_premium: false });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async (track_id, name) => {
    if (!window.confirm(`Remove "${name}" from the library?`)) return;
    try {
      await axios.delete(`${API}/music/library/${track_id}`);
      toast.success('Removed');
      load();
    } catch { toast.error('Failed to remove'); }
  };

  const grouped = tracks.reduce((acc, t) => {
    const g = t.mood || 'upbeat'; acc[g] = acc[g] || []; acc[g].push(t); return acc;
  }, {});

  return (
    <div data-testid="music-library-tab">
      {/* Upload form */}
      <div className="rounded-2xl border border-white/10 bg-[#15161B] p-6 mb-8" data-testid="music-upload-form">
        <h3 className="text-lg font-bold mb-1 flex items-center gap-2"><Microphone size={18} weight="bold" /> Add a Track</h3>
        <p className="text-xs text-gray-500 mb-4">Upload a royalty-free MP3 (max 25 MB). Every host on EventDrop can pick from this shared library when generating their Film or Highlight Reel.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Track name *</label>
            <input
              type="text" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Sunset Memories"
              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#B89762]/30"
              data-testid="music-track-name"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Mood</label>
            <select
              value={form.mood}
              onChange={(e) => setForm({ ...form, mood: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm text-white focus:outline-none"
              data-testid="music-track-mood"
            >
              {MOODS.map(m => <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Attribution (optional)</label>
            <input
              type="text" value={form.attribution}
              onChange={(e) => setForm({ ...form, attribution: e.target.value })}
              placeholder="Artist / source"
              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm text-white focus:outline-none"
              data-testid="music-track-attribution"
            />
          </div>
        </div>
        <input
          ref={fileRef} type="file"
          accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/m4a,audio/mp4,.mp3,.wav,.ogg,.m4a"
          className="hidden" onChange={handleUpload}
          data-testid="music-file-input"
        />
        <label className="flex items-center gap-2 mb-3 cursor-pointer select-none text-xs text-gray-400 hover:text-white">
          <input type="checkbox" checked={form.is_premium}
            onChange={e => setForm({ ...form, is_premium: e.target.checked })}
            className="rounded border-white/20 bg-black/40"
            data-testid="music-premium-checkbox" />
          <span>Premium track — locked behind Pro Upgrade (£9.99 unlock)</span>
        </label>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading || !form.name.trim()}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white text-sm disabled:opacity-40"
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          data-testid="music-upload-btn"
        >
          <Upload size={16} weight="bold" /> {uploading ? 'Uploading…' : 'Upload MP3'}
        </button>
      </div>

      {/* Existing tracks */}
      <div className="rounded-2xl border border-white/10 bg-[#15161B] overflow-hidden" data-testid="music-library-list">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-lg font-bold">Library ({tracks.length})</h3>
          <button onClick={load} className="text-xs text-gray-400 hover:text-white flex items-center gap-1" data-testid="music-library-refresh">
            <ArrowClockwise size={14} weight="bold" /> Refresh
          </button>
        </div>
        {loading ? (
          <div className="py-12 text-center text-gray-500">Loading…</div>
        ) : tracks.length === 0 ? (
          <div className="py-12 text-center text-gray-500" data-testid="music-library-empty">
            Library is empty. Upload your first track above.
          </div>
        ) : (
          Object.entries(grouped).sort().map(([mood, list]) => (
            <div key={mood} data-testid={`music-group-${mood}`}>
              <div className="px-6 py-2 bg-black/20 text-xs font-bold uppercase tracking-wider text-gray-400">{mood} · {list.length}</div>
              {list.map((t) => (
                <div key={t.track_id} className="px-6 py-3 flex items-center gap-4 border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`music-row-${t.track_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{t.name}</p>
                    <p className="text-xs text-gray-500">
                      {t.duration_sec ? `${Math.round(t.duration_sec)}s` : 'unknown length'}
                      {t.attribution ? ` · ${t.attribution}` : ''}
                      {t.size_bytes ? ` · ${Math.round(t.size_bytes / 1024)} KB` : ''}
                    </p>
                  </div>
                  <audio controls src={`${API}/music/library/${t.track_id}/stream`} className="h-8 max-w-[240px]" />
                  <button
                    onClick={() => handleDelete(t.track_id, t.name)}
                    className="p-2 rounded-lg hover:bg-red-500/10 text-red-400"
                    title="Remove"
                    data-testid={`music-delete-${t.track_id}`}
                  >
                    <Trash size={16} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════
// TEMPLATE LIBRARY TAB (shared across all events)
// ═══════════════════════════════════════
const TemplateLibraryTab = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'generic' });
  const fileRef = useRef(null);

  const CATS = ['generic', 'wedding', 'birthday', 'christmas', 'halloween', 'corporate', 'festival', 'western', 'retro', 'elegant'];

  const load = async () => {
    try { const r = await axios.get(`${API}/template-library`); setTemplates(r.data || []); }
    catch { toast.error('Failed to load library'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!form.name.trim()) { toast.error('Enter a name first'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (10 MB max)'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', form.name);
      fd.append('category', form.category);
      await axios.post(`${API}/template-library`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Added "${form.name}"`);
      setForm({ name: '', category: form.category });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async (template_id, name) => {
    if (!window.confirm(`Remove "${name}" from the library?`)) return;
    try { await axios.delete(`${API}/template-library/${template_id}`); toast.success('Removed'); load(); }
    catch { toast.error('Failed to remove'); }
  };

  const grouped = templates.reduce((acc, t) => { const g = t.category || 'generic'; acc[g] = acc[g] || []; acc[g].push(t); return acc; }, {});

  return (
    <div data-testid="template-library-tab">
      {/* Upload form */}
      <div className="rounded-2xl border border-white/10 bg-[#15161B] p-6 mb-8" data-testid="template-upload-form">
        <h3 className="text-lg font-bold mb-1 flex items-center gap-2"><PaintBrush size={18} weight="bold" /> Add a Background Template</h3>
        <p className="text-xs text-gray-500 mb-4">Upload a JPG/PNG themed background (max 10 MB). Hosts pick from this shared library in the Photobooth Template Editor. Square images work best (1200×1200+).</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-500 mb-1">Template name *</label>
            <input type="text" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Christmas Sparkle, Rustic Wedding"
              className="w-full px-4 py-2.5 rounded-xl bg-[#15161B] border border-white/10 text-white text-sm focus:outline-none focus:border-[#B89762]/40"
              data-testid="template-name-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Category</label>
            <select value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl bg-[#15161B] border border-white/10 text-white text-sm focus:outline-none focus:border-[#B89762]/40"
              data-testid="template-category-select">
              {CATS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
            </select>
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleUpload} />
        <button onClick={() => fileRef.current?.click()} disabled={uploading || !form.name.trim()}
          className="w-full py-3 rounded-xl font-bold text-white text-sm disabled:opacity-50 transition-all hover:scale-[1.01]"
          style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #FFCC00 100%)' }}
          data-testid="template-upload-btn">
          {uploading ? 'Uploading...' : 'Choose image & upload'}
        </button>
      </div>

      {/* Template grid */}
      <div className="rounded-2xl border border-white/10 bg-[#15161B] p-6" data-testid="template-list">
        <h3 className="text-lg font-bold mb-4">Library ({templates.length})</h3>
        {loading ? (
          <div className="py-10 text-center text-gray-500 text-sm">Loading...</div>
        ) : templates.length === 0 ? (
          <div className="py-10 text-center text-gray-500 text-sm">No backgrounds yet — upload one above.</div>
        ) : (
          Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([cat, list]) => (
            <div key={cat} className="mb-6">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">{cat} ({list.length})</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {list.map(t => (
                  <div key={t.template_id} className="relative group rounded-xl overflow-hidden aspect-square" style={{ background: '#15161B' }} data-testid={`template-card-${t.template_id}`}>
                    <img src={`${API}/template-library/${t.template_id}/image`} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 p-2.5" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)' }}>
                      <p className="text-white text-xs font-semibold truncate">{t.name}</p>
                    </div>
                    <button onClick={() => handleDelete(t.template_id, t.name)}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500/90 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`template-delete-${t.template_id}`}>
                      <Trash size={12} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════
// MAIN ADMIN PANEL
// ═══════════════════════════════════════
const AdminPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => { fetchOverview(); }, []);

  const fetchOverview = async () => {
    try {
      const res = await axios.get(`${API}/admin/overview`);
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load admin data');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#B89762]/40 border-t-transparent"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'general', label: 'General', icon: <ChartBar size={16} weight="bold" /> },
    { id: 'design', label: 'Design & Branding', icon: <PaintBrush size={16} weight="bold" /> },
    { id: 'music', label: 'Music Library', icon: <Microphone size={16} weight="bold" /> },
    { id: 'templates', label: 'Template Library', icon: <PaintBrush size={16} weight="bold" /> },
  ];

  return (
    <div className="min-h-screen bg-[#15161B] text-white overflow-x-hidden" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft size={20} weight="bold" />
            <span className="font-medium">Back to Dashboard</span>
          </button>
        </div>

        <div className="mb-8">
          <h1 className="text-5xl font-bold tracking-tight mb-2" data-testid="admin-title">Admin Panel</h1>
          <p className="text-gray-500">Manage your events and customize your guestbook screen</p>
        </div>

        {/* Tabs — horizontally scrollable on mobile so all 4 always reachable */}
        <div className="flex items-center gap-1 mb-8 border-b border-white/10 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0" style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }} data-testid="admin-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-shrink-0 flex items-center gap-2 px-4 sm:px-5 py-3 text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'text-[#C29D52] border-[#B89762]/40'
                  : 'text-gray-500 border-transparent hover:text-gray-300 hover:border-white/20'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'general' && (
          <GeneralTab data={data} navigate={navigate} fetchOverview={fetchOverview} />
        )}
        {activeTab === 'design' && (
          <DesignTab events={data?.events || []} />
        )}
        {activeTab === 'music' && (
          <MusicLibraryTab />
        )}
        {activeTab === 'templates' && (
          <TemplateLibraryTab />
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
